import { __ } from "@wordpress/i18n";
import React, { useState, useEffect, useRef } from 'react';
import Hint from '../components/Hint/Hint';
import InfoBox from '../components/InfoBox/InfoBox';
import InputBox from '../components/InputBox/InputBox';
import Switch from '../components/Switch/Switch';
import TagInput from '../components/TagInput/TagInput';
import SingleSelect from '../components/SingleSelect/SingleSelect';
import MediaUploader from '../components/MediaUploader/MediaUploader';
import { useMenu } from "../contexts/MenuContext";
import withForm from '../layouts/withForm';
import apiFetch from '@wordpress/api-fetch';

const RestrictionsSettings = ({handleChange}) => {
    const {settingData} = useMenu();
    const tagInputRef = useRef();
    const [redirectTo, setRedirectTo] = useState(settingData?.restrictions?.settings?.redirect_to || '');
    const [pages, setPages] = useState([]);

    useEffect(() => {
        const fetchPages = async () => {
            try {
                const response = await apiFetch({ path: '/wp/v2/pages' });
                setPages(response);
            } catch (error) {
                // console.error('Error fetching pages:', error);
            }
        };
        fetchPages();
    }, []);
    
    useEffect(() => {
        if (settingData.restrictions.settings.firewall == 1) {
            renderCurrentIP();
        }
    }, [settingData.restrictions.settings.firewall]);

    const pageOptions = pages.map((page) => ({
        label: page.title.rendered || `(no title) - ID ${page.id}`,
        value: page.id.toString(),
    }));
    const renderCurrentIP = () => {
        const API_URL = "/ultimate-security/v1/get-ip";
        apiFetch({ path: API_URL, method: 'GET' })
            .then(response => {
                if (!response?.ip) return;
                const currentIP = response.ip;
    
                // Get current tags from context (via settingData)
                const currentTags = settingData?.restrictions?.settings?.whitelisted_ips || [];
    
                // Check for duplicates
                const alreadyExists = currentTags.some(tag => tag?.value === currentIP);
                if (alreadyExists) {
                    // console.log("IP already exists in whitelist");
                    return;
                }
    
                // Use the imperative handle to add the tag directly to the TagInput
                if (tagInputRef.current) {
                    const newTag = { value: currentIP };
                    tagInputRef.current.addTag(newTag);
                } else {
                    console.error("TagInput ref not available");
                }
            })
            .catch(error => {
                console.error("Error fetching IP:", error);
            });
    };
    const handleRedirectToChange = (name, value) => {
        setRedirectTo(value);
        handleChange('restrictions.settings.redirect_to', value);
    };
   
    return (
        <>
            <div className="setting-unit pb-lg mb-lg">
                <div className="row">
                    <div className="col-lg-5">
                        <div className="title-group">
                            <h5 className="pl-heading-1 fw-600 text-text">{__("Restrict site access to visitors who aren't logged in or allowed by IP address", "ultimate-security")}</h5>
                        </div>
                    </div>
                    <div className="col-lg-7">
                        <div className="row justify-content-between">
                            <div className="col-lg-10">
                                <div className="title-group d-flex align-items-center ">
                                    <Hint position="top" content={__("Enable/disable restrictions", "ultimate-security")}/>
                                    <h6 className="pl-heading-1 fw-600 text-text">{__("Enable", "ultimate-security")}</h6>
                                </div>
                               
                            </div>
                            
                            <div className="col-auto">
                                <Switch 
                                    name="restrictions.settings.firewall"
                                    checked={settingData?.restrictions?.settings?.firewall}
                                    onChange={handleChange} 
                                    valueType="string"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="setting-unit pb-lg mb-lg">
                <div className="row">
                    <div className="col-lg-5">
                        <div className="title-group">
                            <h5 className="pl-heading-1 fw-600 text-text">{__("Handle restricted visitors", "ultimate-security")}</h5>
                        </div>
                    </div>
                    <div className="col-lg-7">
                        <div className="row justify-content-between">
                            <div className="col-lg-10">
                                <div className="title-group d-flex align-items-center ">
                                    <Hint position="top" content={__("Redirect restricted visitors", "ultimate-security")}/>
                                    <SingleSelect
                                        options={[
                                            { label: 'Send them to the WordPress login screen', value: 'login_screen' },
                                            { label: 'Redirect them to a specified web address', value: 'web_address' },
                                            { label: 'Show them a simple message', value: 'message' },
                                            { label: 'Show them a page', value: 'page' }
                                        ]}
                                        initialValue={settingData?.restrictions?.settings?.redirect_to || 'login_screen'}
                                        name="restrictions.settings.redirect_to"
                                        selected={settingData?.restrictions?.settings?.redirect_to}
                                        handleChange={handleRedirectToChange}
                                    />
                                </div>
                                
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {redirectTo === 'web_address' && (
                <>
                    <div className="setting-unit pb-lg mb-lg">
                        <div className="row">
                            <div className="col-lg-5">
                                <div className="title-group">
                                    <h5 className="pl-heading-1 fw-600 text-text">{__("Redirect web address", "ultimate-security")}</h5>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="row justify-content-between">
                                    <div className="col-lg-10">
                                        <div className="title-group d-flex align-items-center">
                                            <Hint position="top" content={__("Redirect web address", "ultimate-security")}/>
                                            <InputBox 
                                                type="url" 
                                                name="restrictions.settings.redirect_web_address" 
                                                placeholder="https://example.com"
                                                value={settingData?.restrictions?.settings?.redirect_web_address} 
                                                handleChange={handleChange} 
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="setting-unit pb-lg mb-lg">
                        <div className="row">
                            <div className="col-lg-5">
                                <div className="title-group">
                                    <h5 className="pl-heading-1 fw-600 text-text">{__("Redirect to same path", "ultimate-security")}</h5>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="row justify-content-between">
                                    <div className="col-lg-10">
                                        <div className="title-group d-flex align-items-center ">
                                            <Hint position="top" content={__("Enable redirect to same path", "ultimate-security")}/>
                                            <h6 className="pl-heading-1 fw-600 text-text">{__("Enable", "ultimate-security")}</h6>
                                        </div>
                                        <div className="content-below-with-hint">                                    
                                            <p className="pl-body text-text">{__( "Send restricted visitor to same path (relative URL) at the new web address", "ultimate-security" )}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="col-auto">
                                        <Switch 
                                            name="restrictions.settings.redirect_to_same_path"
                                            checked={settingData?.restrictions?.settings?.redirect_to_same_path} // Pass "1"/"0" from API 
                                            onChange={handleChange} 
                                            valueType="string"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="setting-unit pb-lg mb-lg">
                        <div className="row">
                            <div className="col-lg-5">
                                <div className="title-group">
                                    <h5 className="pl-heading-1 fw-600 text-text">{__("Redirection status code", "ultimate-security")}</h5>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="row justify-content-between">
                                    <div className="col-lg-10">
                                        <div className="title-group d-flex align-items-center ">
                                            <Hint position="top" content={__("Redirect status code", "ultimate-security")}/>
                                            <SingleSelect
                                                options={[
                                                    { label: '302 undefined', value: '302' },
                                                    { label: '301 permanent', value: '301' },
                                                    { label: '307 temporary', value: '307' },
                                                ]}
                                                initialValue={settingData?.restrictions?.settings?.redirection_status_code || '302'}
                                                name="restrictions.settings.redirection_status_code"
                                                selected={settingData?.restrictions?.settings?.redirection_status_code}
                                                handleChange={handleChange}
                                            />
                                        </div>
                                        
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </>
            )}
            {redirectTo === 'message' && (
                <>
                    <div className="setting-unit pb-lg mb-lg">
                        <div className="row">
                            <div className="col-lg-5">
                                <div className="title-group">
                                    <h5 className="pl-heading-1 fw-600 text-text">{__("Header background image", "ultimate-security")}</h5>                         
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="row justify-content-between">
                                    <div className="col-lg-10">                                
                                        <MediaUploader 
                                            data={settingData?.restrictions?.settings?.header_background_image} 
                                            name='restrictions.settings.header_background_image' 
                                            handleChange={handleChange}
                                            options = {{
                                                frame:{
                                                    title: __("Select or Upload Image", "ultimate-security"),
                                                },
                                                library: {type: 'image'},
                                                buttons: {
                                                    upload: __("Upload Image", "ultimate-security"),
                                                    remove: __("Remove", "ultimate-security"),
                                                    select: __("Use this image", "ultimate-security")                                            
                                                },
                                                hint: __("No Hint", "ultimate-security"),
                                                message: {
                                                    size: 'Size: Recommended 420x420 px <br> File Support: png',
                                                    info: 'Select the background image for the header'
                                                } 
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> 

                    <div className="setting-unit pb-lg mb-lg">
                        <div className="row">
                            <div className="col-lg-5">
                                <div className="title-group">
                                    <h5 className="pl-heading-1 fw-600 text-text">{__("Restriction message", "ultimate-security")}</h5>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="row justify-content-between">
                                    <div className="col-lg-10">
                                        <div className="title-group d-flex align-items-center">
                                            <Hint position="top" content={__("Restriction message", "ultimate-security")}/>
                                            <textarea 
                                                className="form-control"
                                                value={settingData?.restrictions?.settings?.restriction_message}
                                                onChange={(e)=> handleChange('restrictions.settings.restriction_message', e.target.value)}
                                            />
                                        </div>
                                        
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {redirectTo === 'page' && (
                <>
                    <div className="setting-unit pb-lg mb-lg">
                        <div className="row">
                            <div className="col-lg-5">
                                <div className="title-group">
                                    <h5 className="pl-heading-1 fw-600 text-text">{__("Restricted notice page", "ultimate-security")}</h5>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="row justify-content-between">
                                    <div className="col-lg-10">
                                        <div className="title-group d-flex align-items-center ">
                                            <Hint position="top" content={__("Restricted notice page", "ultimate-security")}/>
                                            <SingleSelect
                                                options={pageOptions}
                                                initialValue={settingData?.restrictions?.settings?.restricted_notice_page || ''}
                                                name="restrictions.settings.restricted_notice_page"
                                                selected={settingData?.restrictions?.settings?.restricted_notice_page}
                                                handleChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <div className="setting-unit pb-lg mb-lg">
                <div className="row">
                    <div className="col-lg-5">
                        <div className="title-group">
                            <h5 className="pl-heading-1 fw-600 text-text">{__("Unrestricted IP Addresses", "ultimate-security")}</h5>
                            <p className="pl-body text-text">{__("Set the list of IPs you would like to allow for your WooCommerce pages.", "ultimate-security")}</p>
                        </div>
                    </div>
                    <div className="col-lg-7">
                        <div className="row justify-content-between">
                            <div className="col-lg-10">
                                <div className="title-group d-flex">
                                    <Hint position="top" content={__("Unrestricted IP Addresses", "ultimate-security")}/>                                    
                                    <TagInput
                                        ref={tagInputRef}
                                        name="restrictions.settings.whitelisted_ips"
                                        type="ip"
                                        initialTags={settingData?.restrictions?.settings?.whitelisted_ips}
                                        handleChange={handleChange}
                                    />
                                    <button 
                                        type="button" 
                                        className="button button-link" 
                                        onClick={renderCurrentIP}
                                    >
                                        {__("Add My Current IP Address", "ultimate-security")}
                                    </button>

                                </div>
                                <div className="content-below-with-hint mt-2">                                    
                                    <InfoBox className="small-infobox" title={__( "Note", "ultimate-security" )} content={__( "Enter a single IP address or a range using a subnet prefix", "ultimate-security" )} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="setting-unit">
                <div className="row">
                    <div className="col-lg-5">
                        <div className="title-group">
                            <h5 className="pl-heading-1 fw-600 text-text">{__("Debug Log On/Off:", "ultimate-security")}</h5>
                            <p className="pl-body text-text">{__("Debug mode adds a top bar for easy switching for both frontend and backend.", "ultimate-security")}</p>
                        </div>
                    </div>
                    <div className="col-lg-7">
                        <div className="row justify-content-between">
                            <div className="col-lg-10">
                                <div className="title-group d-flex align-items-center ">
                                    <Hint position="top" content={__("Show/hide top debug bar", "ultimate-security")}/>
                                    <h6 className="pl-heading-1 fw-600 text-text">{__("Debug bar", "ultimate-security")}</h6>
                                </div>
                                <div className="content-below-with-hint">                                    
                                    <p className="pl-body text-text">{__( "Enable/disable debug bar", "ultimate-security" )}</p>
                                </div>
                            </div>
                            
                            <div className="col-auto">
                                <Switch 
                                    name="restrictions.settings.debug_log"
                                    checked={settingData?.restrictions?.settings?.debug_log} // Pass "1"/"0" from API 
                                    onChange={handleChange} 
                                    valueType="string"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
export default withForm(RestrictionsSettings, 'restrictions.settings');